<script setup lang="ts">
definePageMeta({ layout: false })

const route = useRoute()
const mode = ref<'password' | 'email-code'>('email-code')

// Si on vient d'un flux OAuth, on garde les params
const oauthRedirect = computed(() => route.query.redirect === 'authorize')
const oauthParams = computed(() => ({
  client_id: route.query.client_id as string,
  redirect_uri: route.query.redirect_uri as string,
  state: route.query.state as string,
}))

// Email pour le flux OTP
const otpEmail = ref('')
const showOtp = ref(false)
// Admin avec passkey : second facteur apres le mot de passe ou le code
const showPasskey = ref(false)

function onPasskeyRequired() {
  showOtp.value = false
  showPasskey.value = true
}

function retour() {
  showOtp.value = false
  showPasskey.value = false
}
// Code demande apres un mot de passe valide, sur un appareil non reconnu
const deviceCheck = ref(false)

function onEmailCodeSent(email: string) {
  otpEmail.value = email
  deviceCheck.value = false
  showOtp.value = true
}

function onOtpRequired(email: string) {
  otpEmail.value = email
  deviceCheck.value = true
  showOtp.value = true
}

async function onAuthSuccess() {
  if (oauthRedirect.value) {
    // Reprendre le flux OAuth
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: oauthParams.value.client_id,
      redirect_uri: oauthParams.value.redirect_uri,
      state: oauthParams.value.state || '',
    })
    await navigateTo(`/api/auth/authorize?${params.toString()}`, { external: true })
  }
  else {
    // Sanitize redirect_after pour eviter les open redirects
    let redirect = route.query.redirect_after as string || '/'
    if (!redirect.startsWith('/') || redirect.startsWith('//')) {
      redirect = '/'
    }
    await navigateTo(redirect)
  }
}

// Annee du pied de page
const year = new Date().getFullYear()

const tabs = [
  { value: 'email-code', icon: 'i-lucide-mail', label: 'Code par email' },
  { value: 'password', icon: 'i-lucide-lock', label: 'Mot de passe' },
] as const

const features = [
  { icon: 'i-lucide-layout-grid', label: 'Retrouvez toutes vos applications internes au même endroit' },
  { icon: 'i-lucide-mouse-pointer-click', label: 'Accédez à chaque outil d\'un simple clic' },
  { icon: 'i-lucide-key-round', label: 'Un seul compte, une seule connexion pour tout' },
]
</script>

<template>
  <div class="relative min-h-screen overflow-hidden bg-gradient-to-br from-white via-slate-50 to-acc-50/60">
    <!-- Cercles concentriques en fond -->
    <div class="pointer-events-none absolute inset-0" aria-hidden="true">
      <div
        v-for="size in [520, 820, 1120, 1420]"
        :key="size"
        class="absolute rounded-full border border-slate-200 bg-white/30"
        :style="{ width: `${size}px`, height: `${size}px`, left: '28%', top: '50%', transform: 'translate(-50%, -50%)' }"
      />
    </div>

    <!-- Bande decorative aux couleurs ACC, bord droit -->
    <div class="pointer-events-none absolute inset-y-0 right-0 hidden w-3 lg:flex flex-col" aria-hidden="true">
      <div class="flex-[3] bg-acc-400" />
      <div class="flex-[2] bg-accdark-500" />
      <div class="flex-1 bg-accyellow-500" />
      <div class="flex-[3] bg-acc-400" />
      <div class="flex-1 bg-accred-700" />
      <div class="flex-[2] bg-accdark-500" />
      <div class="flex-[3] bg-acc-400" />
    </div>

    <div class="relative mx-auto flex min-h-screen max-w-6xl flex-col px-4 py-8 lg:pr-20">
      <!-- Logo ACC, en tete de page, hors de l'alignement presentation / formulaire -->
      <img src="/images/logo-vertical.svg" alt="ACC Sud-Ouest" width="1227" height="1244" class="apparition hidden lg:block h-52 w-auto self-start mt-28">

      <div class="flex flex-1 items-center">
        <!-- Haut de la presentation aligne sur le haut du formulaire -->
        <div class="flex w-full items-start justify-center gap-16 lg:justify-between">
          <!-- Presentation, a gauche (masquee sur mobile) -->
          <section class="apparition hidden lg:block max-w-lg pt-8">
            <div class="flex items-center gap-3 mb-3">
              <UIcon name="i-lucide-shield-check" class="text-5xl text-accdark-500" />
              <p class="text-4xl font-bold tracking-tight text-accdark-500">ACC Connect</p>
            </div>
            <p class="text-xl text-slate-600 mb-10">
              Le hub de connexion à tous les outils de l'ACC&nbsp;Sud&#8209;Ouest.
            </p>
            <ul class="space-y-4 text-lg text-slate-700">
              <li v-for="feature in features" :key="feature.icon" class="flex items-center gap-4">
                <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-acc-50 ring-1 ring-acc-100">
                  <UIcon :name="feature.icon" class="text-xl text-acc-600" />
                </span>
                {{ feature.label }}
              </li>
            </ul>
          </section>

          <!-- Zone de connexion, a droite -->
          <div class="apparition apparition-retard w-full max-w-md">
            <!--
              Les SVG du logo n'ont qu'une viewBox : sans width/height, Safari
              leur donne une largeur nulle avec `w-auto` et le logo disparait.
            -->
            <!-- Ecran etroit : logo ACC et nom de l'application au-dessus du formulaire, qui reste identique au desktop -->
            <div class="mb-8 flex flex-col items-center gap-12 lg:hidden">
              <img src="/images/logo-horizontal.svg" alt="ACC Sud-Ouest" width="2784" height="653" class="h-12 w-auto">
              <div class="flex items-center gap-2">
                <UIcon name="i-lucide-shield-check" class="text-3xl text-accdark-500" />
                <span class="text-2xl font-bold tracking-tight text-accdark-500">ACC Connect</span>
              </div>
            </div>
            <section class="rounded-xl bg-white p-8 shadow-xl shadow-slate-300/40 ring-1 ring-slate-200">
              <div class="text-center mb-6">
                <h1 class="text-xl font-semibold text-slate-800">
                  Bienvenue
                </h1>
                <p class="mt-1 text-sm text-slate-500">
                  Connectez-vous avec votre compte ACC pour continuer.
                </p>
              </div>

              <div v-if="showOtp || showPasskey" class="flex items-center gap-2 mb-4">
                <UButton variant="ghost" color="neutral" size="xs" icon="i-lucide-arrow-left" aria-label="Retour" @click="retour" />
                <span class="text-sm font-medium text-slate-700">{{ showPasskey ? 'Validation par passkey' : 'Vérification du code' }}</span>
              </div>

              <!-- Sélecteur de mode, segmente -->
              <div v-if="!showOtp && !showPasskey" role="tablist" class="grid grid-cols-2 gap-1 mb-6 rounded-lg bg-slate-100 p-1">
                <button
                  v-for="tab in tabs"
                  :key="tab.value"
                  type="button"
                  role="tab"
                  :aria-selected="mode === tab.value"
                  class="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-sm sm:gap-2 sm:px-3 font-medium transition-all focus-visible:outline-2 focus-visible:outline-acc-500"
                  :class="mode === tab.value
                    ? 'bg-white text-accdark-500 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-500 hover:text-slate-700'"
                  @click="mode = tab.value"
                >
                  <UIcon :name="tab.icon" class="text-base" />
                  {{ tab.label }}
                </button>
              </div>

              <!-- Formulaires -->
              <PasskeyStep v-if="showPasskey" @success="onAuthSuccess" />
              <OtpForm v-else-if="showOtp" :email="otpEmail" :device-check="deviceCheck" @success="onAuthSuccess" @passkey-required="onPasskeyRequired" />
              <EmailCodeForm v-else-if="mode === 'email-code'" :client-id="oauthParams.client_id" @sent="onEmailCodeSent" />
              <LoginForm v-else @success="onAuthSuccess" @otp-required="onOtpRequired" @passkey-required="onPasskeyRequired" />

              <p class="mt-6 border-t border-slate-100 pt-4 text-center text-xs text-slate-500">
                Accès réservé aux utilisateurs autorisés
              </p>
            </section>

          </div>
        </div>
      </div>

      <!-- Pied de page : en bas de la page, centre -->
      <p class="mt-10 text-center text-xs text-slate-400">
        © {{ year }} ACC Sud&#8209;Ouest
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Apparition douce : fondu et leger glissement, desactive si l'utilisateur limite les animations */
.apparition {
  animation: apparition 400ms ease-out both;
}

.apparition-retard {
  animation-delay: 120ms;
}

@keyframes apparition {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .apparition {
    animation: none;
  }
}
</style>
